import * as React from 'react'
import { LocationSearchInput } from './Autocomplete/Autocomplete.container'
import { Block, Division, Text } from '$views/components'
import { Icon } from '$views/components/Icon'
import { StyledLink } from '$views/components/List/UnordererList/UnorderedList.style'
import { getBrowserPosition } from '$utils/services/geolocationBrowserService'
import { getAddressFromLocation } from '$services/map/browserAddress/browserAddress.service'
import { draft } from '$utils/services/formDraftService'
import { LoadGoogleMapsApi } from '$utils/loadGoogleMapsApi'
import { formatError } from '$views/components/Error/Error.mapper'
import { ErrorView } from '$views/components/Error/Error.model'
import { Error } from '$views/components/Error'
import { Spinner } from '$views/components/Spinner'
import { geocodeByAddress } from 'react-places-autocomplete'
import { Location } from '$domain/model/map/map.model'
import { ErrorText } from '$views/components/Input'
import { getProvince } from '$utils/locationFormatter'

const locationIcon = require('$icons/location.svg')

interface Props {
  draftName: string
  draftPropertyName: string
  errorText?: any
  label?: string
  updateAddressForExternalValidation?: (address) => void
}

export const LocationInput: React.FC<Props> = ({
  draftName,
  draftPropertyName,
  errorText,
  label,
  updateAddressForExternalValidation,
}) => {
  const [isLoading, handleLoading] = React.useState<boolean>(true)
  const [address, setAddress] = React.useState<string>('')
  const [errorNotification, handleErrorNotification] = React.useState<ErrorView>()
  const [showErrorNotification, handleShowErrorNotification] = React.useState<boolean>(false)

  const onErrorNotificationClick = () => handleShowErrorNotification(false)

  const loadInitialLocation = () => {
    const draftValues = draft.get(draftName)
    if (draftValues && draftValues[draftPropertyName] && draftValues[draftPropertyName].address) {
      setAddress(draftValues[draftPropertyName].address)
      updateAddressForExternalValidation(draftValues[draftPropertyName].address)
    }
  }

  React.useEffect(() => {
    const loadMap = async () => {
      try {
        await LoadGoogleMapsApi.get({
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_CLIENT,
          v: '3.exp',
          libraries: ['geometry', 'drawing', 'places'],
        })
        handleLoading(false)
      } catch (error) {
        const googleMapError = formatError(error)
        handleErrorNotification(googleMapError)
        handleShowErrorNotification(true)
        handleLoading(false)
      }
    }

    loadMap()
    loadInitialLocation()
  }, [])

  const saveLocationDraft = (locationResult: Location) => draft.set(draftName, { [draftPropertyName]: locationResult })

  const handleLocation = async selectedAddress => {
    setAddress(selectedAddress)
    const results = await geocodeByAddress(selectedAddress)
    results[0].formatted_address = selectedAddress
    saveLocationDraft({
      latitude: results[0].geometry.location.lat(),
      longitude: results[0].geometry.location.lng(),
      address: results[0].formatted_address,
      province: getProvince(results[0]),
    })
    updateAddressForExternalValidation(selectedAddress)
  }

  const onLocalizeMe = async () => {
    let location
    try {
      const browserPosition = await getBrowserPosition()
      const coordinates = {
        lat: browserPosition.coords.latitude,
        lng: browserPosition.coords.longitude,
      }
      location = await getAddressFromLocation(coordinates.lat, coordinates.lng)
    } catch (error) {
      const coordinates = {
        lat: parseFloat(process.env.REACT_APP_GOOGLE_MAPS_PERMISSION_ERROR_DEFAULT_LAT as string),
        lng: parseFloat(process.env.REACT_APP_GOOGLE_MAPS_PERMISSION_ERROR_DEFAULT_LNG as string),
      }
      location = await getAddressFromLocation(coordinates.lat, coordinates.lng)
    }
    setAddress(location.formattedAddress)
    saveLocationDraft(location)
    updateAddressForExternalValidation(location.formattedAddress)
  }

  if (isLoading) {
    return <Spinner />
  }

  if (showErrorNotification) {
    return <Error error={errorNotification} onClick={onErrorNotificationClick} />
  }

  return (
    <>
      <LocationSearchInput address={address} updateAddress={setAddress} onSelect={handleLocation} />
      {label && (
        <Text menu marginBottom="mini">
          {label}
        </Text>
      )}
      <Block flex alignItems="center" justify="end">
        <Icon content={locationIcon} size="m" color="darkGrey" />
        <StyledLink href="#" onClick={onLocalizeMe}>
          Localiza mi posición
        </StyledLink>
      </Block>
      {errorText && (
        <>
          <ErrorText caption error>
            {errorText}
          </ErrorText>
          <Division color="darkPink" />
        </>
      )}
    </>
  )
}



// WEBPACK FOOTER //
// ./src/views/components/Forms/LocationInput/LocationInput.tsx